/**
 * Console Capture Service
 * Intercepts console.log/warn/error/info and stores them in a buffer
 * for bug report submission.
 */

const logBuffer = [];
const MAX_ENTRIES = 100;
const MAX_AGE_MS = 60000; // 1 minute

export function initConsoleCapture() {
  const originalMethods = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info
  };

  Object.keys(originalMethods).forEach(method => {
    console[method] = (...args) => {
      logBuffer.push({
        level: method,
        message: args.map((arg) => {
          if (typeof arg !== 'object' || arg == null) return String(arg);
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }).join(' '),
        timestamp: new Date().toISOString(),
        source: new Error().stack?.split('\n')[2] || 'unknown'
      });

      // Remove old entries
      const cutoff = Date.now() - MAX_AGE_MS;
      while (logBuffer.length > 0 &&
             new Date(logBuffer[0].timestamp).getTime() < cutoff) {
        logBuffer.shift();
      }
      // Enforce max entries
      while (logBuffer.length > MAX_ENTRIES) {
        logBuffer.shift();
      }

      // Call original method
      originalMethods[method].apply(console, args);
    };
  });

  // Capture unhandled errors
  window.addEventListener('error', (event) => {
    logBuffer.push({
      level: 'error',
      message: `Uncaught: ${event.message}`,
      source: `${event.filename}:${event.lineno}:${event.colno}`,
      timestamp: new Date().toISOString()
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logBuffer.push({
      level: 'error',
      message: `Unhandled Promise: ${event.reason}`,
      source: 'promise',
      timestamp: new Date().toISOString()
    });
  });
}

export function getConsoleLogs() {
  return [...logBuffer];
}
